import React, {useRef, useEffect, useState, useMemo} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Box, Stack, Popper, Typography, ClickAwayListener, Skeleton } from '@mui/material'
import { gql, useQuery } from '@apollo/client'
import h337 from 'heatmap.js';

import Player from './Player'
import { useMap } from 'packages/wimap/Indoor/IndoorProvider'

interface Position {
  x: number
  y: number
}

interface MapContextValue {
  ratio: number
  transformRef?: React.RefObject<HTMLElement>
  convertPosToMapBase: (position: Position) => Position
}

const OPERATION_GRAPH = gql`
  query GraphConnectedStationsByMacs($sf_id: ID!, $macs: [String]!, $period: String) {
    aps: graphConnectedStationsByMacs(sf_id: $sf_id, macs: $macs, period: $period) {
      mac
      stamp
      connected_stations
    }
  }
`

const PERIODS = [{
  value: '-1d', label: 'heatmap.period.1d'
}, {
  value: '-3d', label: 'heatmap.period.3d'
}, {
  value: '-7d', label: 'heatmap.period.7d'
}]

const Period = ({period, onChange}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const {t} = useTranslation()
  const labelPeriod = useMemo(() => {
    const label = PERIODS.find(({value}) => value === period)?.label
    return label ? t(label) : period
  }, [period, t])
  return (
    <>
      <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
        <Stack
          direction={'row'}
          alignItems={'center'}
          gap={0.5}
          sx={{
            cursor: 'pointer',
            color: '#475467',
            flex: '0 0 auto',
            whiteSpace: 'nowrap',
            pr: 0.25
          }}
          onClick={(e) => {
            setAnchorEl(anchorEl ? null : e.currentTarget)
          }}>
          <i className="fa-solid fa-clock" style={{fontSize: '11px', transform: 'translateY(-1px)'}} />
          <Typography
            variant={'caption'}
            sx={{fontSize: 12, lineHeight: 1, textWrap: 'nowrap'}}
          >
            {labelPeriod}
          </Typography>
          <i className={'fa-regular fa-angle-down'} style={{fontSize: '10px'}} />
        </Stack>
      </ClickAwayListener>
      <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} sx={{zIndex: 120}}>
        <Stack
          sx={{
            cursor: 'pointer',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 24px rgba(30, 41, 59, 0.18)',
            mt: 1,
            minWidth: 112
          }}
          padding={1}>
          {
            PERIODS.map(({value, label}) => (
              <Typography
                variant={'caption'}
                color={value === period ? 'primary' : 'text.primary'}
                key={value}
                onClick={() => {
                  setAnchorEl(null)
                  onChange(value)
                }}
                sx={{
                  borderRadius: '6px',
                  px: 1,
                  py: 0.5,
                  '&:hover': {
                    backgroundColor: '#F2F4F7'
                  }
                }}>
                {t(label)}
              </Typography>
            ))
          }
        </Stack>
      </Popper>
    </>
  )
}

export const Heatmap = ({ image, devices }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const heatmapInstance = useRef<ReturnType<typeof h337.create> | null>(null);
  const [period, setPeriod] = useState('-1d')
  const [indexData, setIndexData] = useState(0)
  const {focusId} = useParams()
  const {t} = useTranslation()
  const { ratio, convertPosToMapBase, transformRef } = useMap() as MapContextValue
  const imageSize = useMemo(() => [image.width * ratio, image.height * ratio], [image, ratio])

  const {data: graph, loading} = useQuery(OPERATION_GRAPH, {
    variables: {
      sf_id: focusId,
      period: period,
      macs: devices.filter(({type}) => type === 'ap').map(({mac}) => mac)
    },
    skip: devices.filter(({type}) => type === 'ap').length === 0
  })
  const data = useMemo(() => {
    if (!graph?.aps?.[0]?.stamp) {
      return []
    }
    return graph.aps[0].stamp.map((date, i) => {
      const values = graph.aps.map(({connected_stations, mac}) => {
        const device = devices.find(device => device.mac === mac)
        const position = convertPosToMapBase(device.position)
        return {
          x: Math.round(position.x),
          y: Math.round(position.y),
          value: connected_stations[i]
        }
      })
      return {
        date,
        values
      }
    })
  }, [devices, convertPosToMapBase, graph])

  const max = useMemo(() => {
    let max = 0
      const currentMax = [...data[indexData]?.values || []].sort((a, b) => b.value - a.value)[0]
      if (currentMax && currentMax.value > max) {
        max = currentMax.value
      }

    return max
  }, [data, indexData])

  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    containerRef.current.innerHTML = ''
    heatmapInstance.current = h337.create({
      container: containerRef.current,
      radius: 52,
      maxOpacity: 0.86,
      minOpacity: 0,
      blur: 0.92,
      gradient: { '.5': 'blue', '.8': 'yellow', '.95': 'red' }
    });
  }, [imageSize]);

  // Mise à jour des données
  useEffect(() => {
    if (!heatmapInstance.current) return;
    
    const filteredData = (data[indexData]?.values || []).filter(point => point.value > 0)
    
    heatmapInstance.current.setData({
      max: max || 1,
      data: filteredData
    });
  }, [loading, data, max, indexData]);

  const player = (
    <Player
      max={data.length}
      onChange={setIndexData}
      period={period}
      timeline={data}
      key={period}>
      <Period
        period={period}
        onChange={value => {
          setIndexData(0)
          setPeriod(value)
        }}
      />
    </Player>
  )

  return (
    <Box
      sx={{
        position: 'relative',
        width: `${imageSize[0]}px`,
        height: `${imageSize[1]}px`,
        boxShadow: `
          0px 1.85px 3.15px 0px rgba(0, 0, 0, 0.08),
          0px 8.15px 6.52px 0px rgba(0, 0, 0, 0.04),
          0px 20px 13px 0px rgba(0, 0, 0, 0.04),
          0px 38.52px 25.48px 0px rgba(0, 0, 0, 0.043),
          0px 64.81px 46.85px 0px rgba(0, 0, 0, 0.055)
        `
      }}>
      {loading ? (
        <>
          <Skeleton variant={'rectangular'} width={'100%'} height={'100%'} sx={{position: 'absolute'}}/>
          <Stack
            direction={'row'}
            justifyContent={'center'}
            alignItems={'center'}
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              zIndex: 2
            }}>
            <Typography sx={{ fontSize: 12 }}>
              {t('ui_elements.loading')}
            </Typography>
          </Stack>
        </>
      ) : (
        <img
          src={image.dataURL}
          alt="floor plan"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            zIndex: 1
          }}
        />
      )}
      {transformRef?.current ? createPortal(player, transformRef.current) : player}
      <Box
        ref={containerRef}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          zIndex: 2,
          pointerEvents: 'none',
          '& canvas': {
            position: 'absolute !important',
            left: 0,
            top: 0,
            pointerEvents: 'none'
          }
        }}
      />
    </Box>
  )
}

export default Heatmap